import {
  ALL_FACES,
  DEFAULT_CONSTANTS,
  FILES,
  Pcg32,
  STREAM_CAMPAIGN,
  baseDeckSpec,
  hex64,
  makeConfig,
  seedToBigInt,
  type Action,
  type CardSpec,
  type Constants,
  type MatchEvent,
  type MatchResult,
  type PawnSpec,
  type PresetId,
  type ReserveSpec,
  type Side,
} from '../engine';
import { generateName } from './names';
import {
  PROFILE_VERSION,
  type FieldedPawn,
  type NamedPawn,
  type PawnRecords,
  type Profile,
  type RosterEntry,
  type RunState,
  type TableOutcome,
} from './types';

/**
 * The campaign layer (spec §15). It is separate from the match engine: it builds match configs
 * for each table and consumes match results. Every operation returns a new profile.
 */

export class CampaignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CampaignError';
  }
}

export const clone = <T>(v: T): T => structuredClone(v);

export const emptyRecords = (): PawnRecords => ({
  tablesPlayed: 0,
  captures: 0,
  timesCaptured: 0,
  epitaphsLeft: 0,
  bestChain: 0,
  crowns: 0,
  timesPawned: 0,
});

/** Draws from the profile's campaign stream and stores the advanced stream back. */
export function withRng<T>(p: Profile, fn: (rng: Pcg32) => T): T {
  const rng = Pcg32.fromState(p.rng);
  const out = fn(rng);
  p.rng = rng.toState();
  return out;
}

export function recruitInto(p: Profile, runNumber: number): NamedPawn {
  const taken = new Set(Object.values(p.pawns).map((x) => x.name));
  const name = withRng(p, (rng) => generateName(rng, taken));
  const id = `P-${String(p.nextPawnNumber).padStart(3, '0')}`;
  p.nextPawnNumber += 1;
  const pawn: NamedPawn = { id, name, joinedRun: runNumber, records: emptyRecords() };
  p.pawns[id] = pawn;
  p.stable.push(id);
  return pawn;
}

export const remove = (list: string[], id: string): void => {
  const i = list.indexOf(id);
  if (i >= 0) list.splice(i, 1);
};

/** A new profile with CAMPAIGN_STARTING_STABLE named pawns (§15.1). */
export function createProfile(seed: string, constants: Constants = DEFAULT_CONSTANTS): Profile {
  const p: Profile = {
    version: PROFILE_VERSION,
    seed,
    rng: Pcg32.seeded(seedToBigInt(seed), STREAM_CAMPAIGN).toState(),
    pawns: {},
    stable: [],
    collection: [],
    nextPawnNumber: 1,
    stats: { runsStarted: 0, runsWon: 0, runsLost: 0, tablesWon: 0, tablesLost: 0 },
    run: null,
    history: [],
  };
  for (let i = 0; i < constants.CAMPAIGN_STARTING_STABLE; i++) recruitInto(p, 0);
  return p;
}

export const isRunActive = (p: Profile): boolean =>
  !!p.run && p.run.phase !== 'won' && p.run.phase !== 'lost';

/** A Long Night run in progress (its roster shares the stable with the classic tables). */
export const isNightActive = (p: Profile): boolean =>
  !!p.ledger?.run && p.ledger.run.phase !== 'won' && p.ledger.run.phase !== 'lost';

/** The run deck: the base deck plus every card bought this run (§15.6 step 5). */
export const runDeck = (run: RunState, constants: Constants = DEFAULT_CONSTANTS): CardSpec[] => [
  ...baseDeckSpec(constants),
  ...run.bought,
];

/** North's head start at a table (§15.2); tables beyond the list reuse its last value. */
export function headStartFor(table: number, constants: Constants = DEFAULT_CONSTANTS): number {
  const list = constants.CAMPAIGN_HEAD_START;
  return list[Math.min(table, list.length) - 1] ?? 0;
}

export const displayName = (p: Profile, id: string): string | null => p.pawns[id]?.name ?? null;

/**
 * Starts a run (§15.2) with pawns chosen from the stable. The player must choose
 * min(CAMPAIGN_FIELDED, stable size) pawns; stand-ins fill any gap (§15.3).
 */
export function startRun(
  profile: Profile,
  chosen: readonly string[],
  constants: Constants = DEFAULT_CONSTANTS,
): Profile {
  if (isRunActive(profile) || isNightActive(profile))
    throw new CampaignError('a run is already in progress');
  const p = clone(profile);
  if (p.run) finishRunInto(p, false);
  const size = constants.CAMPAIGN_FIELDED;
  const needed = Math.min(size, p.stable.length);
  const unique = new Set(chosen);
  if (unique.size !== chosen.length) throw new CampaignError('a pawn was chosen twice');
  if (chosen.length !== needed)
    throw new CampaignError(`choose exactly ${needed} pawns from the stable`);
  for (const id of chosen)
    if (!p.stable.includes(id)) throw new CampaignError(`${id} is not in the stable`);
  const roster: RosterEntry[] = chosen.map((id) => ({ id, standIn: false }));
  let standInSeq = 0;
  while (roster.length < size) roster.push({ id: `SI-${++standInSeq}`, standIn: true });
  p.stats.runsStarted += 1;
  p.run = {
    number: p.stats.runsStarted,
    table: 1,
    phase: 'fielding',
    roster,
    bought: [],
    match: null,
    offers: [],
    standInSeq,
    outcomes: [],
  };
  return p;
}

export interface TableOptions {
  readonly preset?: Exclude<PresetId, 'CUSTOM'>;
  readonly constants?: Constants;
}

/**
 * Fields six roster pawns on chosen files and sets up the table's match (§15.3, §15.7). Former pawns
 * are drawn from the Broker's collection and placed with the campaign stream.
 */
export function fieldTable(
  profile: Profile,
  fielding: readonly FieldedPawn[],
  opts: TableOptions = {},
): Profile {
  const constants = opts.constants ?? DEFAULT_CONSTANTS;
  const run = profile.run;
  if (!run || run.phase !== 'fielding')
    throw new CampaignError('no table is waiting to be fielded');
  if (fielding.length !== 6) throw new CampaignError('field exactly six pawns');
  const ids = new Set(fielding.map((f) => f.id));
  const files = new Set(fielding.map((f) => f.file));
  if (ids.size !== 6) throw new CampaignError('a pawn was fielded twice');
  if (
    files.size !== 6 ||
    fielding.some((f) => !Number.isInteger(f.file) || f.file < 0 || f.file > 5)
  )
    throw new CampaignError('each fielded pawn needs its own file, A–F');
  for (const f of fielding)
    if (!run.roster.some((r) => r.id === f.id))
      throw new CampaignError(`${f.id} is not on the roster`);

  const p = clone(profile);
  const r = p.run as RunState;
  const table = r.table;
  const k = Math.min(constants.CAMPAIGN_FORMER_PAWNS_MAX, p.collection.length);
  const { formers, northFiles, seed } = withRng(p, (rng) => {
    const formers = rng.shuffle([...p.collection]).slice(0, k);
    const northFiles = rng.shuffle([0, 1, 2, 3, 4, 5]);
    const seed = hex64(rng.nextU64());
    return { formers, northFiles, seed };
  });
  const north: PawnSpec[] = northFiles.map((file, i) => {
    const former = formers[i];
    if (former) return { id: former, file, name: p.pawns[former]?.name ?? former, former: true };
    return { id: `N-${FILES[file]}`, file };
  });
  north.sort((a, b) => a.file - b.file);
  const standIns = new Set(r.roster.filter((x) => x.standIn).map((x) => x.id));
  const south: PawnSpec[] = [...fielding]
    .sort((a, b) => a.file - b.file)
    .map((f) =>
      standIns.has(f.id)
        ? { id: f.id, file: f.file, standIn: true }
        : { id: f.id, file: f.file, name: p.pawns[f.id]?.name ?? f.id },
    );
  const reserves: ReserveSpec[] = r.roster
    .filter((x) => !ids.has(x.id))
    .map((x) =>
      x.standIn ? { id: x.id, standIn: true } : { id: x.id, name: p.pawns[x.id]?.name ?? x.id },
    );
  const config = makeConfig({
    mode: 'campaign',
    preset: opts.preset ?? constants.DEFAULT_PRESET,
    northHeadStart: headStartFor(table, constants),
    constants,
    seating: { players: ['player', `broker-table-${table}`], assignment: 'fixed' },
    decks: { S: runDeck(r, constants) },
    pawns: { S: south, N: north },
    reserves: { S: reserves },
  });
  r.match = {
    config,
    seed,
    actions: [],
    regular: table - 1,
    formerPawns: formers,
    fielded: fielding.map((f) => f.id),
  };
  r.phase = 'playing';
  return p;
}

/** Appends an applied action to the table's replay, so the match can be resumed (§16.5). */
export function recordAction(profile: Profile, action: Action): Profile {
  const run = profile.run;
  if (!run || run.phase !== 'playing' || !run.match)
    throw new CampaignError('no table is being played');
  return {
    ...profile,
    run: { ...run, match: { ...run.match, actions: [...run.match.actions, action] } },
  };
}

/** Everything the campaign needs from a finished table match. */
export interface TableResult {
  readonly result: MatchResult;
  /** Final total scores. */
  readonly scores: Readonly<Record<Side, number>>;
  /** South's pawns (board or reserve) used as Wild collateral during the match. */
  readonly collateralUsed: readonly string[];
  /** Every event of the match, for pawn records. */
  readonly events: readonly MatchEvent[];
}

export function applyRecords(p: Profile, events: readonly MatchEvent[]): void {
  const rec = (id: string): PawnRecords | undefined => p.pawns[id]?.records;
  for (const e of events) {
    switch (e.type) {
      case 'PawnCaptured': {
        const victim = rec(e.pawnId);
        if (victim) {
          victim.timesCaptured += 1;
          if (e.epitaphId) victim.epitaphsLeft += 1;
        }
        const capturer = rec(e.byPawnId);
        if (capturer) capturer.captures += 1;
        break;
      }
      case 'ChainScored': {
        const r = e.source === 'chain' ? rec(e.pawnId) : undefined;
        if (r) r.bestChain = Math.max(r.bestChain, e.breakdown.score);
        break;
      }
      case 'PawnCrowned': {
        const r = rec(e.pawnId);
        if (r) r.crowns += 1;
        break;
      }
      case 'PawnPawned': {
        const r = rec(e.pawnId);
        if (r) r.timesPawned += 1;
        break;
      }
      default:
        break;
    }
  }
}

/** Resolves a finished table (§15.4–§15.6). */
export function finishTable(
  profile: Profile,
  outcome: TableResult,
  constants: Constants = DEFAULT_CONSTANTS,
): Profile {
  const run0 = profile.run;
  if (!run0 || run0.phase !== 'playing' || !run0.match)
    throw new CampaignError('no table is being played');
  const p = clone(profile);
  const run = p.run as RunState;
  const match = run.match as NonNullable<RunState['match']>;
  const won = outcome.result.winner === 'S';
  applyRecords(p, outcome.events);
  for (const id of match.fielded) {
    const r = p.pawns[id]?.records;
    if (r) r.tablesPlayed += 1;
  }
  for (const id of match.formerPawns) {
    const r = p.pawns[id]?.records;
    if (r) r.tablesPlayed += 1;
  }
  const onRoster = new Set(run.roster.map((r) => r.id));
  const marked = [...new Set(outcome.collateralUsed)].filter((id) => onRoster.has(id));
  const isStandIn = (id: string) => run.roster.some((r) => r.id === id && r.standIn);
  const base = {
    table: run.table,
    won,
    result: outcome.result,
    scores: { S: outcome.scores.S, N: outcome.scores.N },
    marked,
  };
  if (!won) {
    // §15.5: marked named pawns go to the Broker; marked stand-ins vanish.
    const lost = marked.filter((id) => !isStandIn(id));
    const vanished = marked.filter(isStandIn);
    for (const id of lost) {
      remove(p.stable, id);
      if (!p.collection.includes(id)) p.collection.push(id);
    }
    run.roster = run.roster.filter((r) => !marked.includes(r.id));
    run.phase = 'lost';
    run.match = null;
    p.stats.tablesLost += 1;
    p.stats.runsLost += 1;
    run.outcomes.push({
      ...base,
      lost,
      vanished,
      reclaimed: [],
      recruited: [],
    } satisfies TableOutcome);
    return p;
  }
  // §15.6: 1. clear marks (they only lived for this table); 2. reclaim; 3. recruit; 4. counter.
  p.stats.tablesWon += 1;
  const reclaimed: string[] = [];
  for (const id of match.formerPawns) {
    if (!p.collection.includes(id)) continue;
    remove(p.collection, id);
    p.stable.push(id);
    reclaimed.push(id);
    const slot = run.roster.findIndex((r) => r.standIn);
    if (slot >= 0) run.roster[slot] = { id, standIn: false };
    else run.roster.push({ id, standIn: false });
  }
  const recruited: string[] = [];
  for (let i = 0; i < constants.CAMPAIGN_RECRUITS_PER_WIN; i++) {
    const pawn = recruitInto(p, run.number);
    recruited.push(pawn.id);
    run.roster.push({ id: pawn.id, standIn: false });
  }
  run.match = null;
  run.outcomes.push({
    ...base,
    lost: [],
    vanished: [],
    reclaimed,
    recruited,
  } satisfies TableOutcome);
  if (run.table >= constants.CAMPAIGN_TABLES) {
    // A purchase after the final table could never be used (§15.8), so the run ends here.
    run.phase = 'won';
    p.stats.runsWon += 1;
    return p;
  }
  const types: CardSpec[] = [
    ...ALL_FACES.map((face) => ({ kind: 'normal' as const, face })),
    { kind: 'wild' },
  ];
  run.offers = withRng(p, (rng) => rng.shuffle(types).slice(0, constants.CAMPAIGN_OFFERS));
  run.phase = 'counter';
  return p;
}

/** Roster pawns that can pay at the Counter: named pawns (stand-ins can't be traded). */
export const tradeablePawns = (p: Profile): string[] =>
  p.run ? p.run.roster.filter((r) => !r.standIn).map((r) => r.id) : [];

export interface Purchase {
  /** Index into run.offers. */
  readonly offer: number;
  /** A named roster pawn paid for the card; it goes to the Broker's collection. */
  readonly payWith: string;
}

/**
 * The Counter (§15.6 steps 4–5): buy any number of offered cards, one reserve each, then reset the
 * deck and move on to the next table. The roster must keep enough pawns to field a table.
 */
export function buyAtCounter(
  profile: Profile,
  purchases: readonly Purchase[],
  constants: Constants = DEFAULT_CONSTANTS,
): Profile {
  const run0 = profile.run;
  if (!run0 || run0.phase !== 'counter') throw new CampaignError('the Counter is not open');
  const offers = new Set(purchases.map((x) => x.offer));
  const payers = new Set(purchases.map((x) => x.payWith));
  if (offers.size !== purchases.length) throw new CampaignError('each offer can be bought once');
  if (payers.size !== purchases.length) throw new CampaignError('each pawn can pay once');
  for (const x of purchases) {
    if (!Number.isInteger(x.offer) || x.offer < 0 || x.offer >= run0.offers.length)
      throw new CampaignError('unknown offer');
    const entry = run0.roster.find((r) => r.id === x.payWith);
    if (!entry) throw new CampaignError(`${x.payWith} is not on the roster`);
    if (entry.standIn) throw new CampaignError('stand-ins cannot be traded');
  }
  if (run0.roster.length - purchases.length < constants.CAMPAIGN_FIELDED)
    throw new CampaignError(`keep at least ${constants.CAMPAIGN_FIELDED} pawns on the roster`);
  const p = clone(profile);
  const run = p.run as RunState;
  for (const x of purchases) {
    run.roster = run.roster.filter((r) => r.id !== x.payWith);
    remove(p.stable, x.payWith);
    if (!p.collection.includes(x.payWith)) p.collection.push(x.payWith);
    run.bought.push(run0.offers[x.offer] as CardSpec);
  }
  run.offers = [];
  run.table += 1;
  run.phase = 'fielding';
  return p;
}

function finishRunInto(p: Profile, abandoned: boolean): void {
  const run = p.run;
  if (!run) return;
  p.history.push({
    number: run.number,
    won: run.phase === 'won',
    tablesWon: run.outcomes.filter((o) => o.won).length,
    abandoned,
  });
  if (p.history.length > 100) p.history.splice(0, p.history.length - 100);
  p.run = null;
}

/** Closes a finished run (won or lost) after its summary has been shown (§15.8). */
export function closeRun(profile: Profile): Profile {
  if (!profile.run) return profile;
  if (isRunActive(profile)) throw new CampaignError('the run is still in progress');
  const p = clone(profile);
  finishRunInto(p, false);
  return p;
}

/**
 * Abandons a run between tables. A table in progress must be conceded through the match engine
 * first, so that its loss is resolved by finishTable.
 */
export function abandonRun(profile: Profile): Profile {
  const run = profile.run;
  if (!run) return profile;
  if (run.phase === 'playing') throw new CampaignError('concede the current table first');
  const p = clone(profile);
  if (isRunActive(p)) p.stats.runsLost += 1;
  finishRunInto(p, true);
  return p;
}

/** Validates an untrusted stored profile; returns null when it cannot be used. */
export function parseProfile(data: unknown): Profile | null {
  if (typeof data !== 'object' || data === null) return null;
  const p = data as Partial<Profile>;
  if (p.version !== PROFILE_VERSION) return null;
  if (typeof p.seed !== 'string' || typeof p.rng !== 'object' || p.rng === null) return null;
  if (typeof p.pawns !== 'object' || p.pawns === null) return null;
  if (!Array.isArray(p.stable) || !Array.isArray(p.collection) || !Array.isArray(p.history))
    return null;
  if (typeof p.nextPawnNumber !== 'number' || typeof p.stats !== 'object' || p.stats === null)
    return null;
  for (const id of [...p.stable, ...p.collection])
    if (typeof id !== 'string' || !p.pawns[id]) return null;
  if (p.run !== null && (typeof p.run !== 'object' || !Array.isArray(p.run?.roster))) return null;
  return p as Profile;
}
